import { useState } from "react";
import { motion } from "framer-motion";
import {
  Bot,
  Briefcase,
  Coffee,
  MessageSquare,
  Package,
  Radio,
  Shirt,
  Smartphone,
  Sparkles,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { AppHeader } from "@/components/sartn/AppHeader";
import { useBusinessStore } from "@/stores/businessStore";
import { useLanguageStore } from "@/stores/languageStore";
import { cn } from "@/lib/cn";

interface ProductCategoryOption {
  value: string;
  label: string;
  labelAm: string;
  icon: LucideIcon;
  tone: string;
}

const CATEGORIES: ProductCategoryOption[] = [
  {
    value: "clothing",
    label: "Clothing",
    labelAm: "ልብስ",
    icon: Shirt,
    tone: "bg-blue-50 text-blue-600",
  },
  {
    value: "food",
    label: "Food & drink",
    labelAm: "ምግብና መጠጥ",
    icon: Coffee,
    tone: "bg-amber-50 text-amber-600",
  },
  {
    value: "electronics",
    label: "Electronics",
    labelAm: "ኤሌክትሮኒክስ",
    icon: Smartphone,
    tone: "bg-violet-50 text-violet-600",
  },
  {
    value: "beauty",
    label: "Beauty",
    labelAm: "ውበት",
    icon: Sparkles,
    tone: "bg-rose-50 text-rose-600",
  },
  {
    value: "services",
    label: "Services",
    labelAm: "አገልግሎት",
    icon: Briefcase,
    tone: "bg-emerald-50 text-emerald-600",
  },
  {
    value: "other",
    label: "Other",
    labelAm: "ሌላ",
    icon: Package,
    tone: "bg-slate-50 text-slate-600",
  },
];

const STAGGER_FIELDS = 0.05;

interface FormErrors {
  name?: string;
  price?: string;
}

function validateProductForm(
  name: string,
  price: string,
  language: "en" | "am",
): FormErrors {
  const errors: FormErrors = {};
  if (name.trim().length < 2) {
    errors.name = localize(
      "Product name must be at least 2 characters",
      "የምርት ስም ቢያንስ 2 ፊደላት መሆን አለበት",
      language,
    );
  }
  const priceNum = Number(price);
  if (!price || isNaN(priceNum) || priceNum <= 0) {
    errors.price = localize(
      "Price must be greater than 0",
      "ዋጋ ከ0 በላይ መሆን አለበት",
      language,
    );
  }
  return errors;
}

function localize(en: string, am: string, language: "en" | "am"): string {
  return language === "am" ? am : en;
}

function FormField({
  label,
  error,
  index,
  children,
}: {
  label: string;
  error?: string;
  index: number;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * STAGGER_FIELDS, duration: 0.2 }}
      className="flex flex-col gap-1.5"
    >
      <label className="text-sm font-medium text-[var(--color-text)]">
        {label}
      </label>
      {children}
      {error && (
        <span className="text-xs text-red-600">{error}</span>
      )}
    </motion.div>
  );
}

const inputClass = cn(
  "w-full rounded-[var(--radius-md)] bg-[var(--color-surface)]",
  "px-3 py-2.5 text-sm text-[var(--color-text)] shadow-sm",
  "placeholder:text-[var(--color-text-muted)]",
  "focus:border-[var(--color-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]",
  "transition-colors",
);

function CategoryPicker({
  value,
  onChange,
  language,
}: {
  value: string;
  onChange: (category: string) => void;
  language: "en" | "am";
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {CATEGORIES.map((categoryOption) => {
        const Icon = categoryOption.icon;
        const isSelected = value === categoryOption.value;

        return (
          <button
            key={categoryOption.value}
            type="button"
            onClick={() => onChange(categoryOption.value)}
            className={cn(
              "flex min-h-[72px] flex-col items-center justify-center gap-1.5 rounded-2xl border px-2 py-2 text-center transition-colors active:scale-[0.98]",
              isSelected
                ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5"
                : "border-[var(--color-border)] bg-[var(--color-surface)]",
            )}
            aria-pressed={isSelected}
          >
            <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-xl", categoryOption.tone)}>
              <Icon className="h-4 w-4" />
            </span>
            <span className="text-[11px] font-semibold leading-tight text-[var(--color-text)]">
              {localize(categoryOption.label, categoryOption.labelAm, language)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function ProductForm() {
  const addProduct = useBusinessStore((state) => state.addProduct);
  const language = useLanguageStore((state) => state.language);

  const [name, setName] = useState("");
  const [nameAmharic, setNameAmharic] = useState("");
  const [price, setPrice] = useState("");
  const [category, setCategory] = useState("clothing");
  const [inStock, setInStock] = useState(true);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  function navigateBack() {
    window.location.hash = "/business";
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    const validationErrors = validateProductForm(name, price, language);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSaving(true);

    try {
      await addProduct({
        name: name.trim(),
        nameAmharic: nameAmharic.trim(),
        price: Number(price),
        currency: "ETB",
        category,
        inStock,
      });
      navigateBack();
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader title={localize("Add product", "ምርት አክል", language)} onBack={navigateBack} />

      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="flex-1 px-4 pb-20 pt-4"
      >
        <div className="mb-5 rounded-3xl bg-[var(--color-surface)] p-4">
          <h2 className="font-headline text-base font-bold text-[var(--color-text)]">
            {localize("One product, everywhere", "አንድ ምርት፣ በሁሉም ቦታ", language)}
          </h2>
          <p className="mt-2 text-sm leading-6 text-[var(--color-text-secondary)]">
            {localize(
              "Save the basic details once. SARTN can use them in bot replies, channel posts, and group answers.",
              "መሠረታዊ መረጃውን አንዴ ያስቀምጡ። SARTN በቦት መልሶች፣ በቻናል ፖስቶችና በቡድን መልሶች ይጠቀማል።",
              language,
            )}
          </p>
          <div className="mt-3 grid grid-cols-3 gap-2 text-[10px] font-medium text-[var(--color-text-secondary)]">
            <span className="flex items-center gap-1 rounded-xl bg-[var(--color-bg)] px-2 py-2">
              <Bot className="h-3 w-3 text-[var(--color-primary)]" />
              {localize("Bot", "ቦት", language)}
            </span>
            <span className="flex items-center gap-1 rounded-xl bg-[var(--color-bg)] px-2 py-2">
              <Radio className="h-3 w-3 text-[var(--color-primary)]" />
              {localize("Channel", "ቻናል", language)}
            </span>
            <span className="flex items-center gap-1 rounded-xl bg-[var(--color-bg)] px-2 py-2">
              <MessageSquare className="h-3 w-3 text-[var(--color-primary)]" />
              {localize("Group", "ቡድን", language)}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <FormField
            label={localize("Product name", "የምርት ስም", language)}
            error={errors.name}
            index={0}
          >
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={localize("e.g. Summer dress", "ለምሳሌ፦ የበጋ ቀሚስ", language)}
              className={inputClass}
            />
          </FormField>

          <FormField label={localize("Amharic name", "የአማርኛ ስም", language)} index={1}>
            <input
              type="text"
              value={nameAmharic}
              onChange={(e) => setNameAmharic(e.target.value)}
              placeholder="ለምሳሌ፦ የበጋ ቀሚስ"
              className={cn(inputClass, "font-[NotoSansEthiopic]")}
            />
            <span className="text-xs text-[var(--color-text-muted)]">
              {localize(
                "Optional. Helps customers who prefer Amharic.",
                "አማራጭ ነው። አማርኛ ለሚመርጡ ደንበኞች ይረዳል።",
                language,
              )}
            </span>
          </FormField>

          <FormField label={localize("Price", "ዋጋ", language)} error={errors.price} index={2}>
            <div className="relative">
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0"
                min="0"
                step="0.01"
                className={cn(inputClass, "pr-12")}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-[var(--color-text-muted)]">
                ETB
              </span>
            </div>
          </FormField>

          <FormField label={localize("Category", "ምድብ", language)} index={3}>
            <p className="mb-1 text-xs leading-5 text-[var(--color-text-muted)]">
              {localize(
                "Pick one category so SARTN can recommend it correctly.",
                "SARTN በትክክል እንዲመክረው አንድ ምድብ ይምረጡ።",
                language,
              )}
            </p>
            <CategoryPicker value={category} onChange={setCategory} language={language} />
          </FormField>

          <FormField label={localize("In stock", "በክምችት አለ", language)} index={4}>
            <button
              type="button"
              onClick={() => setInStock(!inStock)}
              className={cn(
                "relative h-7 w-12 rounded-full transition-colors",
                inStock ? "bg-[var(--color-primary)]" : "bg-[var(--color-border)]",
              )}
              role="switch"
              aria-checked={inStock}
            >
              <span
                className={cn(
                  "absolute left-0.5 top-0.5 h-6 w-6 rounded-full bg-white transition-transform",
                  inStock && "translate-x-5",
                )}
              />
            </button>
          </FormField>

          <motion.button
            type="submit"
            disabled={isSaving}
            whileTap={{ scale: 0.97 }}
            className={cn(
              "mt-4 w-full rounded-[var(--radius-md)] py-3 text-sm font-semibold text-white",
              "transition-colors active:scale-[0.97]",
              isSaving
                ? "cursor-not-allowed bg-[var(--color-primary)]/60"
                : "bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)]",
            )}
          >
            {isSaving
              ? localize("Saving...", "በማስቀመጥ ላይ...", language)
              : localize("Save product", "ምርት አስቀምጥ", language)}
          </motion.button>
        </form>
      </motion.main>
    </div>
  );
}
